import {
    CustomerReportResponse,
    EnrichedApiGatewayStatus,
    EnrichedFileSystemWorkerStatus,
    EnrichedQueueStatus,
    EnrichedQueuedEventsErrorStatus
} from "../../../generated-internal-monitoring/api";

export interface CustomerReportFilter {
    responsibleManagers: string[],
    addCompaniesWithoutResponsibleManager: boolean
}

export function createCustomerReport(resp: any) {
    let report = {} as CustomerReportResponse;

    let filteredReport = applyCustomerReportFilter(resp)

    // Handle apiGatewayStatus
    if (filteredReport.apiGatewayStatus) {
        let apiGatewayStatus = filteredReport.apiGatewayStatus as EnrichedApiGatewayStatus;
        apiGatewayStatus.companyApiStatus = sortApiStatus(filteredReport.apiGatewayStatus.companyApiStatus);
        report.apiGatewayStatus = apiGatewayStatus;
    }

    // Handle fileSystemWorkerStatus
    if (filteredReport.fileSystemWorkerStatus) {
        let fileHealthReport = filteredReport.fileSystemWorkerStatus;
        fileHealthReport.sftpSyncResults = sortSFTPStatus(filteredReport.fileSystemWorkerStatus.sftpSyncResults || []);
        fileHealthReport.customerOldFiles = sortFileHeathReport(filteredReport.fileSystemWorkerStatus.customerOldFiles || []);
        fileHealthReport.logeniosOldFiles = sortFileHeathReport(filteredReport.fileSystemWorkerStatus.logeniosOldFiles || []);
        fileHealthReport.attachmentOldFiles = sortFileHeathReport(filteredReport.fileSystemWorkerStatus.attachmentOldFiles || []);
        report.fileSystemWorkerStatus = fileHealthReport;
    }

    // Handle queueStatus
    if (filteredReport.queueStatus) {
        report.queueStatus = {companies: sortQueueStatus(filteredReport.queueStatus.companies || [])};
    }

    // Handle queuedEventsErrorStatus
    if (filteredReport.queuedEventsErrorStatus) {
        report.queuedEventsErrorStatus = {companies: sortQueuedEventsErrorStatus(filteredReport.queuedEventsErrorStatus.companies || [])};
    }

    // Handle statusJsonReport
    if (filteredReport.statusJsonReport) {
        report.statusJsonReport = sortStatusJsonReportCompanies(filteredReport.statusJsonReport || []);
    }

    // Handle needsVerificationFilesCount
    if (filteredReport.needsVerificationFilesCount) {
        report.needsVerificationFilesCount = sortNeedsVerificationFilesCount(filteredReport.needsVerificationFilesCount || []);
    }

    // Handle errorFilesCount
    if (filteredReport.errorFilesCount) {
        report.errorFilesCount = sortNeedsVerificationFilesCount(filteredReport.errorFilesCount || []);
    }

    // Handle etaCalculatorHealth
    if (filteredReport.etaCalculatorHealth) {
        report.etaCalculatorHealth = sortEtaCalculatorWorkersByUnprocessedEvents(filteredReport.etaCalculatorHealth || []);
    }

    // Handle messageLogHealth
    if (filteredReport.messageLogHealth) {
        report.messageLogHealth = sortMessageLogReport(filteredReport.messageLogHealth || {});
    }

    // Handle schedulerReportTaskStatus
    if (filteredReport.schedulerReportTaskStatus) {
        report.schedulerReportTaskStatus = sortSchedulerReportTaskStatus(filteredReport.schedulerReportTaskStatus || {});
    }

    return report;
}

function sortSchedulerReportTaskStatus(schedulerReportTaskStatus: any) {
    schedulerReportTaskStatus?.companies?.sort((a, b) => {
        return b.tasks.length - a.tasks.length
    })
    return schedulerReportTaskStatus
}

function sortMessageLogReport(messageLogHealth: any) {
    messageLogHealth?.companies?.sort((a, b) => {
        return a.totalSendErrors - b.totalSendErrors
    })
    return messageLogHealth
}

function sortEtaCalculatorWorkersByUnprocessedEvents(etaCalculatorHealth: any) {
    etaCalculatorHealth?.workerThreads?.sort((a, b) => {
        if (a.healthy !== b.healthy) {
            return a.healthy ? 1 : -1; // unhealthy (false) zuerst
        }
        return b.numUnprocessedEvents - a.numUnprocessedEvents;
    })
    return etaCalculatorHealth
}

function sortNeedsVerificationFilesCount(list) {
    list?.companies?.sort((a, b) => {
        return b.filesOlderThanFiveMinutes - a.filesOlderThanFiveMinutes
    })
    return list
}

function sortStatusJsonReportCompanies(statusJsonReport) {
    statusJsonReport?.companies?.sort((a, b) => {
        return a.healthy - b.healthy
    })
    return statusJsonReport
}

function sortQueueStatus(list) {
    list?.sort((a, b) => {
        return b.unprocessedEventsOlderThanFiveMinutes - a.unprocessedEventsOlderThanFiveMinutes
    })
    return list
}

function sortQueuedEventsErrorStatus(list) {
    list?.sort((a, b) => {
        return b.processedEventsWithErrorCount - a.processedEventsWithErrorCount
    })
    return list
}

function sortFileHeathReport(list) {
    list?.sort((a, b) => {
        return (b.pendingRetryLoopEntries?.length ?? 0) - (a.pendingRetryLoopEntries?.length ?? 0)
    })?.sort((a, b) => {
        return b.oldFileAmount - a.oldFileAmount
    })
    return list
}

function sortSFTPStatus(list) {
    list?.sort((a, b) => {
        let aVal = 0
        let bVal = 0

        switch (a.status) {
            case "SUCCESS": {
                aVal = 2;
                break;
            }
            case "ERROR": {
                aVal = 1;
                break;
            }
        }

        switch (b.status) {
            case "SUCCESS": {
                bVal = 2;
                break;
            }
            case "ERROR": {
                bVal = 1;
                break;
            }
        }
        // console.log("SORT: a: " + aVal + " b: " + bVal)

        return aVal - bVal
    })
    return list
}

function sortApiStatus(list) {
    list?.sort((a, b) => {
        let aVal = 0
        let bVal = 0

        switch (a.status) {
            case "OK": {
                aVal = 3;
                break;
            }
            case "WARNING": {
                aVal = 2;
                break;
            }
            case "ERROR": {
                aVal = 1;
                break;
            }
        }

        switch (b.status) {
            case "OK": {
                bVal = 3;
                break;
            }
            case "WARNING": {
                bVal = 2;
                break;
            }
            case "ERROR": {
                bVal = 1;
                break;
            }
        }
        // console.log("SORT: a: " + aVal + " b: " + bVal)

        return aVal - bVal
    })
    return list
}

function applyCustomerReportFilter(
    customerReport: CustomerReportResponse
) {
    let storedFilter = JSON.parse(localStorage.getItem("customerReportFilter")) as CustomerReportFilter;
    if (!storedFilter) {
        storedFilter = {
            responsibleManagers: [],
            addCompaniesWithoutResponsibleManager: true
        } as CustomerReportFilter
        localStorage.setItem("customerReportFilter", JSON.stringify(storedFilter))
    }
    let addCompaniesWithoutResponsibleManager = storedFilter.addCompaniesWithoutResponsibleManager ?? true
    let responsibleManagers = storedFilter.responsibleManagers ?? [];

    let filteredReport = {} as CustomerReportResponse;
    if (responsibleManagers.length > 0 || !addCompaniesWithoutResponsibleManager) {
        filteredReport.apiGatewayStatus = filterApiGatewayStatusByResponsibleManager(
            customerReport.apiGatewayStatus,
            responsibleManagers,
            addCompaniesWithoutResponsibleManager
        );
        filteredReport.fileSystemWorkerStatus = filterFileSystemWorkerStatus(
            customerReport.fileSystemWorkerStatus,
            responsibleManagers,
            addCompaniesWithoutResponsibleManager
        );
        filteredReport.queueStatus = filterQueueStatus(
            customerReport.queueStatus,
            responsibleManagers,
            addCompaniesWithoutResponsibleManager
        );
        filteredReport.queuedEventsErrorStatus = filterQueuedEventsErrorStatus(
            customerReport.queuedEventsErrorStatus,
            responsibleManagers,
            addCompaniesWithoutResponsibleManager
        );
        filteredReport.statusJsonReport = filterStatusJsonReportCompanies(
            customerReport.statusJsonReport,
            responsibleManagers,
            addCompaniesWithoutResponsibleManager
        );
        filteredReport.needsVerificationFilesCount = filterNeedsVerificationFilesCount(
            customerReport.needsVerificationFilesCount,
            responsibleManagers,
            addCompaniesWithoutResponsibleManager
        );
        filteredReport.errorFilesCount = filterErrorFilesCount(
            customerReport.errorFilesCount,
            responsibleManagers,
            addCompaniesWithoutResponsibleManager
        );
        filteredReport.etaCalculatorHealth = filterEtaCalculatorHealth(
            customerReport.etaCalculatorHealth,
            responsibleManagers,
            addCompaniesWithoutResponsibleManager
        );
        filteredReport.messageLogHealth = filterMessageLogHealth(
            customerReport.messageLogHealth,
            responsibleManagers,
            addCompaniesWithoutResponsibleManager
        );
        filteredReport.schedulerReportTaskStatus = filterSchedulerReportTaskStatus(
            customerReport.schedulerReportTaskStatus,
            responsibleManagers,
            addCompaniesWithoutResponsibleManager
        );


    } else {
        filteredReport = customerReport;
    }

    return filteredReport;
}

function filterApiGatewayStatusByResponsibleManager(
    apiGatewayStatus: EnrichedApiGatewayStatus,
    responsibleManagers: string[],
    addCompaniesWithoutResponsibleManager: boolean = true
) {
    let result = {} as EnrichedApiGatewayStatus
    if (responsibleManagers.length > 0) {
        result.companyApiStatus = apiGatewayStatus?.companyApiStatus.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []

        let totalWaitingMessages = 0
        result.companyApiStatus.map((item) => {
            totalWaitingMessages += item.externalApiWaitingMessages
        })
        result.totalWaitingMessages = totalWaitingMessages
        let totalPolledMessages = 0
        result.companyApiStatus.map((item) => {
            totalPolledMessages += item.polledExternalApiMessages
        })
        result.totalPolledMessages = totalPolledMessages
    } else {
        result = apiGatewayStatus
    }
    return result
}

function filterFileSystemWorkerStatus(
    fileSystemWorkerStatus: EnrichedFileSystemWorkerStatus,
    responsibleManagers: string[],
    addCompaniesWithoutResponsibleManager: boolean = true
) {
    let result = {} as EnrichedFileSystemWorkerStatus
    if (responsibleManagers.length > 0) {
        result.sftpSyncResults = fileSystemWorkerStatus?.sftpSyncResults?.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []
        result.customerOldFiles = fileSystemWorkerStatus?.customerOldFiles?.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []
        result.logeniosOldFiles = fileSystemWorkerStatus?.logeniosOldFiles?.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []
        result.attachmentOldFiles = fileSystemWorkerStatus?.attachmentOldFiles?.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []

    } else {
        result = fileSystemWorkerStatus
    }
    return result
}

function filterQueueStatus(
    queueStatus: EnrichedQueueStatus,
    responsibleManagers: string[],
    addCompaniesWithoutResponsibleManager: boolean = true
) {
    let result = {} as EnrichedQueueStatus
    if (responsibleManagers.length > 0) {
        result.companies = queueStatus?.companies.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []
    } else {
        result = queueStatus
    }
    return result
}

function filterQueuedEventsErrorStatus(
    queuedEventsErrorStatus: EnrichedQueuedEventsErrorStatus,
    responsibleManagers: string[],
    addCompaniesWithoutResponsibleManager: boolean = true
) {
    let result = {} as EnrichedQueuedEventsErrorStatus
    if (responsibleManagers.length > 0) {
        result.companies = queuedEventsErrorStatus?.companies.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []
    } else {
        result = queuedEventsErrorStatus
    }
    return result
}

function filterStatusJsonReportCompanies(
    statusJsonReport: any,
    responsibleManagers: string[],
    addCompaniesWithoutResponsibleManager: boolean = true
) {
    let result = {} as any
    if (responsibleManagers.length > 0) {
        result.companies = statusJsonReport?.companies?.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []
        let notIgnoredCompanies = result.companies.filter((item) => {
            return item.ignoreStatus?.enabled != true
        })
        result.unhealthyCompanies = notIgnoredCompanies.filter((item) => {
            return !item.healthy
        }).length
        result.totalCompaniesChecked = result.companies.length
        result.companiesMissingStatusJsonFile = notIgnoredCompanies.filter((item) => {
            return !item.statusJsonExists
        }).length
        result.companiesWithLastRunOld = notIgnoredCompanies.filter((item) => {
            return item.lastRunTooOld
        }).length
        result.companiesWithoutRecentFileUpdate = notIgnoredCompanies.filter((item) => {
            return item.lastFileUpdateTooOld
        }).length

    } else {
        result = statusJsonReport
    }
    return result
}

function filterNeedsVerificationFilesCount(
    needsVerificationFilesCount: any,
    responsibleManagers: string[],
    addCompaniesWithoutResponsibleManager: boolean = true
) {
    let result = {} as any
    if (responsibleManagers.length > 0) {
        result.companies = needsVerificationFilesCount?.companies?.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []
        result.totalCompaniesChecked = result.companies.length
        result.filesOlderThanFiveMinutes = result.companies.map((item) => {
            return item.filesOlderThanFiveMinutes
        }).reduce((a, b) => a + b, 0)
    } else {
        result = needsVerificationFilesCount
    }
    return result
}

function filterErrorFilesCount(
    errorFilesCount: any,
    responsibleManagers: string[],
    addCompaniesWithoutResponsibleManager: boolean = true
) {
    let result = {} as any
    if (responsibleManagers.length > 0) {
        result.companies = errorFilesCount?.companies?.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []
        result.output = errorFilesCount?.output

        result.filesOlderThanFiveMinutes = result.companies.map((item) => {
            return item.filesOlderThanFiveMinutes
        }).reduce((a, b) => a + b, 0)
        result.healthy = result.filesOlderThanFiveMinutes == 0
    } else {
        result = errorFilesCount
    }
    return result
}

function filterEtaCalculatorHealth(
    etaCalculatorHealth: any,
    responsibleManagers: string[],
    addCompaniesWithoutResponsibleManager: boolean = true
) {
    let result = {} as any
    if (responsibleManagers.length > 0) {
        result.workerThreads = etaCalculatorHealth?.workerThreads?.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []
        result.databaseQueueSize = etaCalculatorHealth?.databaseQueueSize
        result.operatorHealthy = etaCalculatorHealth?.operatorHealthy
        result.orsHealthy = etaCalculatorHealth?.orsHealthy

    } else {
        result = etaCalculatorHealth
    }
    return result
}

function filterMessageLogHealth(
    messageLogHealth: any,
    responsibleManagers: string[],
    addCompaniesWithoutResponsibleManager: boolean = true
) {
    let result = {} as any
    if (responsibleManagers.length > 0) {
        result.companies = messageLogHealth?.companies?.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []
        result.totalCompaniesChecked = result.companies.length
        result.totalSendErrors = result.companies.map((item) => {
            return item.totalSendErrors
        }).reduce((a, b) => a + b, 0)
    } else {
        result = messageLogHealth
    }
    return result
}

function filterSchedulerReportTaskStatus(
    schedulerReportTaskStatus: any,
    responsibleManagers: string[],
    addCompaniesWithoutResponsibleManager: boolean = true
) {
    let result = {} as any
    if (responsibleManagers.length > 0) {
        result.companies = schedulerReportTaskStatus?.companies?.filter((item) => {
            return filterResponsibleManagers(item.responsibleManager, responsibleManagers, addCompaniesWithoutResponsibleManager)
        }) ?? []
        result.healthy = (result.companies?.filter((item) => {
            return item.state != "OK" && item.state != null
        }).length == 0) || true
    } else {
        result = schedulerReportTaskStatus
    }
    return result
}


function filterResponsibleManagers(
    item: string | null | undefined | "",
    responsibleManagers: string[],
    addCompaniesWithoutResponsibleManager: boolean = true
) {
    let included = responsibleManagers.includes(item)
    if (addCompaniesWithoutResponsibleManager) {
        return included || item == null || item == ""
    }
    return included
}